import React, { useState, useRef, useEffect } from 'react';
import { GatsbyImage } from "gatsby-plugin-image";
import classnames from 'classnames';
import { useIntl, defineMessages } from 'react-intl';
import { Language } from 'enums/types';
import useOnClickOutside from 'hooks/useOnClickOutside';
import accessibleDropdownKeyboardHandler from 'helpers/accessibleDropdownKeyboardHandler';
import * as style from './menu.module.scss';

interface ExternalProduct {
	title: string;
	subtitle: string;
	link: string;
	icon: string;
}

interface Props {
	externalProducts: ExternalProduct[];
}

const translations = defineMessages({
	description: {
		id: 'accessibilityLabel.externalProductsDropdown',
		defaultMessage: 'Toggle external products dropdown',
	},
});

const ExternalProducts = ({ externalProducts }: Props) => {
	const { locale, formatMessage } = useIntl();

    const [isOpenDropdown, setIsOpenDropdown] = useState(false);
	const [activeLink, setActiveLink] = useState(-1);

	const containerRef = useRef<HTMLDivElement>(null);
	const buttonRef = useRef<HTMLButtonElement>(null);
	const linkRefs = useRef<HTMLAnchorElement[]>([]);
	useOnClickOutside(containerRef, () => setIsOpenDropdown(false));

	useEffect(() => {
		if (!isOpenDropdown) {
			setActiveLink(-1);
		}
	}, [isOpenDropdown]);

	return (
        <div
			className={classnames(style.moreOptions, {
				[style.moreOptionsForArabicLang]: locale === Language.Arabic
			})}
			ref={containerRef}
			onKeyDown={(e) => {
				if (!buttonRef.current || !linkRefs.current) {
					return;
				}
				accessibleDropdownKeyboardHandler(e, buttonRef.current, linkRefs.current, isOpenDropdown, setIsOpenDropdown, activeLink, setActiveLink)
			}}
		>
			<div className={style.cubesWrapper}>
				<button
					className={classnames(style.cubes, {
						[style.animateCubes]: !isOpenDropdown && style.animateCubes
					})}
					onClick={() => setIsOpenDropdown(!isOpenDropdown)}
					onFocus={() => setActiveLink(-1)}
					ref={buttonRef}
					aria-haspopup={true}
					aria-expanded={isOpenDropdown}
					aria-label={formatMessage(translations.description)}
				>
					<span className={classnames(style.cube, style.cube1)} />
					<span className={classnames(style.cube, style.cube2)} />
					<span className={classnames(style.cube, style.cube3)} />
					<span className={classnames(style.cube, style.cube4)} />
				</button>
			</div>
			<div className={style.popoverWrapper}>
				<ul
					className={classnames(
						style.externalSubMenu,
						style.moreOptionsBox,
						style.popoverBox,
						{
							[style.rightAlignment]: locale === Language.Arabic,
							[style.show]: isOpenDropdown
						}
					)}
				>
					{externalProducts.map((item: ExternalProduct, index: number) => (
						<li
							key={item.title}
							className={classnames(
								style.optionsListItem,
								{
									[style.textRight]: locale === Language.Arabic,
									[style.polishPadding]: locale === Language.Polish,
								}
							)}
						>
							<a
								href={item.link}
								target="_blank"
								rel="noopener noreferrer"
								onFocus={() => setActiveLink(index)}
								ref={(instance: HTMLAnchorElement) => {
									if (index === 0) {
										linkRefs.current.length = 0;
									}
									linkRefs.current[index] = instance;
								}}
							>
								<div>
									<span className={classnames(
										style.linkIcon, {
											[style.floatRight]: locale === Language.Arabic,
										})}>
										<GatsbyImage image={item.icon} alt="item icon" />
									</span>
									<p className={classnames(style.linkTitle, {
										[style.marginForArabicLang]: locale === Language.Arabic,
									})}>{item.title}</p>
									<p className={classnames(style.linkSubtitle, {
										[style.marginForArabicLang]: locale === Language.Arabic,
									})}>{item.subtitle}</p>
								</div>
							</a>
						</li>
					))}
				</ul>
			</div>
		</div>
    );
};

export default ExternalProducts;
